import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import Questionnaire from "../../../../libs/api/services/Questionnaire";
import GridContainer from "./components/containers/GridContainer";
import ViewerLineItemsContainer from "./components/containers/ViewerLineItemsContainer";
import DatePickerComponent from "./components/widgets/DatePickerComponent";
import DisplayTextComponent from "./components/widgets/DisplayTextComponent";
import DynamicComponentsTable from "./components/widgets/DynamicComponentsTable";
import FacilitiesComponent from "./components/widgets/FacilitiesComponent";
import MultiSelectionComponent from "./components/widgets/MultiSelectionComponent";
import SingleSelectionComponent from "./components/widgets/SingleSelectionComponent";
import TextBoxComponent from "./components/widgets/TextBoxComponent";
import TextInputComponent from "./components/widgets/TextInputComponent";

export default forwardRef(function QuestionnaireViewer({
    assignmentId,
    onViewerReady,
    extra: {
        anonymousAssignment = false,
        mode = undefined, // admin, undefined,explore
        accessTypes = ["View", "Edit", "Admin"],
    } = {},
}, ref) {

    const [sections, setSections] = useState([]);
    const [questionnaireData, setQuestionnaireData] = useState({});
    const readyCalled = useRef(false);
    // showWeightsSlider is managed by parent when relocating the UI

    // Calculate total weights for all answered components
    const calculateTotalWeights = () => {
        let totalWeight = 0; // Total weight for answered components (sum of selected option weights)
        let totalWeightQuestionnaire = 0; // Total weight for the entire questionnaire (sum of max weight per component)

        sections.forEach((section) => {
            section.components.forEach((component) => {
                let componentMaxWeight = 0;
                let selectedWeight = 0;

                if (Array.isArray(component.properties)) {
                    const optionsProperty = component.properties.find((a) => a.property === "Options");
                    if (optionsProperty && optionsProperty.values) {
                        try {
                            const options = JSON.parse(optionsProperty.values);

                            // Compute max weight for this component
                            Object.keys(options).forEach((key) => {
                                const option = options[key];
                                const w = parseFloat(option.weight) || 0;
                                if (w > componentMaxWeight) componentMaxWeight = w;
                            });

                            // Compute selected weight based on stored answer
                            const userAnswer = questionnaireData?.[component.componentId]?.value;
                            if (userAnswer) {
                                if (component.componentType === "Single Selection") {
                                    let _userAnswer = userAnswer;
                                    if (
                                        typeof _userAnswer === "string" &&
                                        (_userAnswer.startsWith("{") || _userAnswer.startsWith("["))
                                    ) {
                                        _userAnswer = JSON.parse(_userAnswer);
                                        if (Object.keys(_userAnswer).length > 0 && _userAnswer.hasOwnProperty("option")) {
                                            _userAnswer = _userAnswer.option;
                                        }
                                    } else if (Array.isArray(_userAnswer)) {
                                        _userAnswer = userAnswer[0];
                                    }

                                    // Find the selected option's weight
                                    Object.keys(options).forEach((key) => {
                                        const option = options[key];
                                        if (option.option === _userAnswer) {
                                            selectedWeight = parseFloat(option.weight) || 0;
                                        }
                                    });
                                } else if (component.componentType === "Multiple Selection") {
                                    let selectedOptions = [];
                                    if (
                                        typeof userAnswer === "string" &&
                                        (userAnswer.startsWith("{") || userAnswer.startsWith("["))
                                    ) {
                                        selectedOptions = JSON.parse(userAnswer);
                                    } else if (Array.isArray(userAnswer)) {
                                        selectedOptions = userAnswer;
                                    }
                                    if (Array.isArray(selectedOptions)) {
                                        selectedOptions.forEach((selectedOption) => {
                                            Object.keys(options).forEach((key) => {
                                                const option = options[key];
                                                if (option.option === selectedOption) {
                                                    selectedWeight += parseFloat(option.weight) || 0;
                                                }
                                            });
                                        });
                                    }
                                }
                            }
                        } catch (e) {
                            // Ignore parsing errors
                        }
                    }
                }

                totalWeight += selectedWeight;
                totalWeightQuestionnaire += componentMaxWeight;
            });
        });

        return { totalWeight, totalWeightQuestionnaire };
    };

    // Get component weight details for the slider
    const getComponentWeightDetails = () => {

        const details = [];
        sections.forEach(section => {
            section.components.forEach(component => {
                const userAnswer = questionnaireData?.[component.componentId]?.value;

                if (userAnswer && Array.isArray(component.properties)) {

                    const optionsProperty = component.properties.find(a => a.property === 'Options');
                    if (optionsProperty && optionsProperty.values) {
                        try {
                            const options = JSON.parse(optionsProperty.values);
                            const componentDetail = {
                                componentId: component.componentId,
                                question: component.question,
                                componentType: component.componentType,
                                selectedOptions: [],
                                totalWeight: 0
                            };
                            if (component.componentType === "Single Selection") {
                                let _userAnswer = userAnswer;
                                // Backwards compatibility
                                if (typeof _userAnswer === 'string' &&
                                    (_userAnswer.startsWith("{") || _userAnswer.startsWith("["))) {
                                    _userAnswer = JSON.parse(_userAnswer);

                                    if (Object.keys(_userAnswer).length > 0 && _userAnswer.hasOwnProperty('option')) {
                                        _userAnswer = _userAnswer.option;
                                    }
                                } else if (Array.isArray(_userAnswer)) {
                                    _userAnswer = userAnswer[0];
                                }

                                Object.keys(options).forEach(key => {
                                    const option = options[key];
                                    if (option.option === _userAnswer && option.weight) {
                                        const weight = parseFloat(option.weight) || 0;
                                        componentDetail.selectedOptions.push({
                                            option: option.option,
                                            weight: weight
                                        });
                                        componentDetail.totalWeight += weight;

                                    }
                                });
                            } else if (component.componentType === "Multiple Selection") {
                                let selectedOptions = [];
                                if (typeof userAnswer === 'string' &&
                                    (userAnswer.startsWith("{") || userAnswer.startsWith("["))) {
                                    selectedOptions = JSON.parse(userAnswer);
                                } else if (Array.isArray(userAnswer)) {
                                    selectedOptions = userAnswer;
                                }
                                if (Array.isArray(selectedOptions)) {
                                    selectedOptions.forEach(selectedOption => {
                                        Object.keys(options).forEach(key => {
                                            const option = options[key];
                                            if (option.option === selectedOption && option.weight) {
                                                const weight = parseFloat(option.weight) || 0;
                                                componentDetail.selectedOptions.push({
                                                    option: option.option,
                                                    weight: weight
                                                });
                                                componentDetail.totalWeight += weight;
                                            }
                                        });
                                    });
                                }
                            }
                            if (componentDetail.selectedOptions.length > 0) {
                                details.push(componentDetail);
                            }
                        } catch (e) {
                            // Ignore parsing errors
                        }
                    }
                }
            });
        });
        return details;
    };

    // Expose helper functions to parent via ref
    useImperativeHandle(ref, () => ({
        calculateTotalWeights: () => calculateTotalWeights(),
        getComponentWeightDetails: () => getComponentWeightDetails(),
    }));
    // Notify parent once viewer has loaded sections and is ready to be queried
    useEffect(() => {
        if (onViewerReady && !readyCalled.current && sections && sections.length > 0) {
            readyCalled.current = true;
            try {
                onViewerReady();
            } catch (e) {
                // swallow callback errors
            }
        }
    }, [onViewerReady, sections]);
    useEffect(() => {
        const loadQuestionnaireData = async () => {
            const _QuestionnaireFull =
                await Questionnaire.GetQuestionnaireResultsFull(
                    assignmentId
                );
            if (!_QuestionnaireFull) {
                console.error(`No questionnaire found for ID: ${assignmentId}`);
                return;
            }
            // Load existing answers if any
            const existing = _QuestionnaireFull.responses.reduce(
                (acc, response) => {
                    if (!response.answerType || !response.answerType.id) {
                        console.warn(
                            `Response ${response.id} does not have a valid answerType or id, skipping.`
                        );
                        return acc;
                    }
                    acc[response.answerType.id] = {
                        questionnaireResponseId: response.id,
                        value: response.answer,
                    };
                    return acc;
                },
                {}
            );
            // console.log(`Existing questionnaire data:`, existing);
            setQuestionnaireData(existing);

            const _Questionnaire = _QuestionnaireFull.questionnaire;
            if (!_Questionnaire) {
                console.error(
                    `No questionnaire found in the response for ID: ${assignmentId}`
                );
                return;
            }

            setSections(
                (_Questionnaire.resources || []).map((resource) => ({
                    id: resource.resource.resource.id,
                    title: resource.resource.resource.name,
                    accessTypes: resource.resource.accessTypes,
                    components:
                        resource.resources.map((componentResource, index) => ({
                            accessTypes: componentResource.AccessTypes,
                            componentId: componentResource.resource.id,
                            componentType: !componentResource.resource.componentType ? '' : componentResource.resource.componentType.type,
                            // Safely resolve properties on the component resource. Some resources may exist
                            // but not have a "properties" array; guard with Array.isArray before using find/filter.
                            layout: (Array.isArray(componentResource.resource?.properties) &&
                                componentResource.resource.properties.find((a) => a.property === "Layout")?.values)
                                ? JSON.parse(componentResource.resource.properties.find((a) => a.property === "Layout").values)
                                : { x: 0, y: index, w: 12, h: 10 }, // Default layout if not specified

                            question: (Array.isArray(componentResource.resource?.properties) &&
                                componentResource.resource.properties.find((a) => a.property === "Question")?.values)
                                ? componentResource.resource.properties.find((a) => a.property === "Question").values
                                : undefined,

                            required: (Array.isArray(componentResource.resource?.properties) &&
                                componentResource.resource.properties.find((a) => a.property === "Required")?.values)
                                ? (JSON.parse(componentResource.resource.properties.find((a) => a.property === "Required").values) ?? false)
                                : false,

                            // Keep properties as an array (possibly empty). Other code checks Array.isArray before using it.
                            properties: Array.isArray(componentResource.resource?.properties)
                                ? componentResource.resource.properties.filter((a) => a.property !== "Layout" && a.property !== "Question")
                                : [],
                        })) || [],
                }))
            );
        };
        loadQuestionnaireData();
    }, [anonymousAssignment, assignmentId]);

    return (
        <div
            className="d-flex flex-column h-100 flex-grow-1 "
            style={{ maxHeight: "100%" }}>
            {sections.map((section, index) => (
                <>
                    <div
                        key={section.id}
                        className={`section text-start`}
                    // style={{
                    //     display: "flex",
                    //     flexDirection: "column",
                    //     flexGrow: 1,
                    //     maxHeight: "100%",
                    // }}
                    >
                        <h2 className="text-center bg-primary-container p-2">{section.title}</h2>
                        <GridContainer
                            handleChangeLayout={() => { }}
                            components={section.components}
                            access={[...section.accessTypes]}>
                            {section.components.map((component, compIndex) => (
                                <ViewerLineItemsContainer component={component} key={component.componentId}>
                                    <div className="">
                                        {{
                                            "Display Text": (
                                                <DisplayTextComponent
                                                    readonly

                                                    onUserInput={() => { }}
                                                    onUserInputComplete={() => { }}
                                                    defaultValues={
                                                        questionnaireData?.[component.componentId]?.value ||
                                                        undefined
                                                    }
                                                    component={component}
                                                />
                                            ),
                                            "Single Selection": (
                                                <SingleSelectionComponent
                                                    readonly
                                                    showStatus
                                                    defaultValues={
                                                        questionnaireData?.[component.componentId]?.value ||
                                                        undefined
                                                    }
                                                    component={component}
                                                />
                                            ),
                                            "Multiple Selection": (
                                                <MultiSelectionComponent
                                                    readonly
                                                    showStatus
                                                    defaultValues={
                                                        (questionnaireData?.[component.componentId]?.value
                                                            && typeof questionnaireData?.[component.componentId]?.value === 'string'
                                                            && (questionnaireData?.[component.componentId]?.value.startsWith("{") || questionnaireData?.[component.componentId]?.value.startsWith("["))
                                                            && JSON.parse(questionnaireData?.[component.componentId]?.value))
                                                        || undefined
                                                    }
                                                    component={component}
                                                />
                                            ),
                                            "Date Picker": (
                                                <DatePickerComponent
                                                    readonly
                                                    defaultValues={
                                                        questionnaireData?.[component.componentId]?.value ||
                                                        undefined
                                                    }
                                                    component={component}
                                                />
                                            ),
                                            "Text Input": (
                                                <TextInputComponent
                                                    readonly

                                                    defaultValues={
                                                        questionnaireData?.[component.componentId]?.value ||
                                                        undefined
                                                    }
                                                    component={component}
                                                />
                                            ),
                                            "Facilities": (
                                                <FacilitiesComponent
                                                    readonly

                                                    defaultValues={
                                                        (questionnaireData?.[component.componentId]?.value
                                                            && typeof questionnaireData?.[component.componentId]?.value === 'string'
                                                            && (questionnaireData?.[component.componentId]?.value.startsWith("{") || questionnaireData?.[component.componentId]?.value.startsWith("["))
                                                            && JSON.parse(questionnaireData?.[component.componentId]?.value))
                                                        || undefined
                                                    }
                                                    component={component}
                                                />
                                            ),
                                            "Get Input text": (
                                                <TextInputComponent
                                                    readonly
                                                    defaultValues={
                                                        questionnaireData?.[component.componentId]?.value ||
                                                        undefined
                                                    }
                                                    component={component}
                                                />
                                            ),
                                            "Dynamic Component Table": (
                                                <DynamicComponentsTable
                                                    readonly

                                                    defaultValues={
                                                        (questionnaireData?.[component.componentId]?.value
                                                            && typeof questionnaireData?.[component.componentId]?.value === 'string'
                                                            && (questionnaireData?.[component.componentId]?.value.startsWith("{") || questionnaireData?.[component.componentId]?.value.startsWith("["))
                                                            && JSON.parse(questionnaireData?.[component.componentId]?.value))
                                                        || undefined
                                                    }
                                                    component={component}
                                                />
                                            ),
                                            "Text Box": (
                                                <TextBoxComponent
                                                    readonly
                                                    onUserInput={() => { }}
                                                    onUserInputComplete={() => { }}
                                                    defaultValues={
                                                        questionnaireData?.[component.componentId]?.value ||
                                                        undefined
                                                    }
                                                    component={component}
                                                />
                                            ),
                                        }[component.componentType] || (
                                                <div className="text-danger">
                                                    Unsupported component type: {component.componentType}
                                                </div>
                                            )}
                                    </div>
                                </ViewerLineItemsContainer>
                            ))}
                        </GridContainer>
                    </div>
                </>
            ))}

            {/* Weights UI relocated to parent (PatientQuestionnaireResults). Helpers are exposed via ref. */}

            {/* CSS for animations */}
            <style jsx>{`
                @keyframes slideUp {
                    from {
                        transform: translateY(100%) scale(0.9);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0) scale(1);
                        opacity: 1;
                    }
                }
                
                /* weights UI moved to parent component */
            `}</style>
        </div>
    )
});
